import { useEffect, useState, type ReactNode } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Tags,
  Truck,
  LogOut,
  Menu,
} from 'lucide-react'
import { ToastProvider, useToast } from '../lib/toast'
import { cn } from '../lib/utils'

interface AdminShared {
  admin?: { name: string; email: string }
  pending_orders_count?: number
  flash: { notice?: string; alert?: string }
  [key: string]: unknown
}

const NAV = [
  { href: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { href: '/admin/orders', label: 'Commandes', icon: ShoppingCart, badge: true },
  { href: '/admin/products', label: 'Produits', icon: Package },
  { href: '/admin/categories', label: 'Catégories', icon: Tags },
  { href: '/admin/shipping_rates', label: 'Livraison', icon: Truck },
]

function FlashBridge() {
  const { flash } = usePage<AdminShared>().props
  const { toast } = useToast()
  useEffect(() => {
    if (flash?.notice) toast(flash.notice, 'success')
    if (flash?.alert) toast(flash.alert, 'error')
  }, [flash?.notice, flash?.alert, toast])
  return null
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { props } = usePage<AdminShared>()
  const current = typeof window !== 'undefined' ? window.location.pathname : ''
  const pending = props.pending_orders_count ?? 0

  const isActive = (href: string, exact?: boolean) =>
    exact ? current === href : current.startsWith(href)

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-6 text-lg font-extrabold">
        Home Couteau
        <span className="ms-2 rounded bg-accent-500 px-1.5 py-0.5 text-[10px] text-white">
          ADMIN
        </span>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV.map((item) => {
          const Icon = item.icon
          const active = isActive(item.href, item.exact)
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              className={cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                active ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100'
              )}
            >
              <Icon className="size-5" />
              <span className="flex-1">{item.label}</span>
              {item.badge && pending > 0 && (
                <span className="rounded-full bg-accent-500 px-2 py-0.5 text-xs font-bold text-white">
                  {pending}
                </span>
              )}
            </Link>
          )
        })}
      </nav>
      <div className="border-t border-ink-100 p-4">
        <div className="mb-2 px-2 text-sm">
          <p className="font-semibold text-ink-900">{props.admin?.name}</p>
          <p className="truncate text-xs text-ink-400">{props.admin?.email}</p>
        </div>
        <button
          onClick={() => router.delete('/admin/logout')}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          <LogOut className="size-5" />
          Déconnexion
        </button>
      </div>
    </div>
  )
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <ToastProvider>
      <FlashBridge />
      <div dir="ltr" className="min-h-screen bg-ink-50 text-start">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 start-0 hidden w-64 border-e border-ink-100 bg-white lg:block">
          <Sidebar />
        </aside>

        {/* Mobile sidebar */}
        {mobileOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-ink-950/40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="fixed inset-y-0 start-0 z-50 w-64 bg-white lg:hidden">
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </aside>
          </>
        )}

        <div className="lg:ps-64">
          <header className="flex h-16 items-center gap-3 border-b border-ink-100 bg-white px-4 lg:hidden">
            <button onClick={() => setMobileOpen(true)} aria-label="menu">
              <Menu className="size-6" />
            </button>
            <span className="font-bold">Admin</span>
          </header>
          <main className="p-4 md:p-8">{children}</main>
        </div>
      </div>
    </ToastProvider>
  )
}
